import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar3, Clock, ExclamationTriangle, Person } from "react-bootstrap-icons";
import SectionHeading from "./SectionHeading";
import useLatestBlogs from "./hooks/useLatestBlogs";
import { ASSETS } from "./data/landingData";

const BlogImage = ({ blog, className }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`lp-blog__media ${className || ""}`}>
      <img
        src={!blog.image || failed ? ASSETS.hero : blog.image}
        alt={blog.title}
        loading="lazy"
        width="800"
        height="500"
        onError={() => setFailed(true)}
      />
      <span className="lp-property__badge">{blog.category}</span>
    </div>
  );
};

const blogHref = (blog) => `/blogs/${blog.slug}`;

const FeaturedCard = ({ blog }) => (
  <article className="lp-card lp-card--lift lp-blog-featured h-100">
    <Link to={blogHref(blog)} state={{ id: blog.id }} className="lp-blog-featured__link" aria-label={`Read ${blog.title}`}>
      <BlogImage blog={blog} className="lp-blog__media--featured" />
      <div className="lp-blog-featured__body">
        <p className="lp-blog__meta">
          <span><Person aria-hidden="true" /> {blog.author}</span>
          {blog.date && <span><Calendar3 aria-hidden="true" /> {blog.date}</span>}
          <span><Clock aria-hidden="true" /> {blog.readTime}</span>
        </p>
        <h3 className="lp-blog-featured__title lp-clamp-2">{blog.title}</h3>
        {blog.excerpt && <p className="lp-muted lp-clamp-3">{blog.excerpt}</p>}
        <span className="lp-link mt-auto">Read Article <ArrowRight aria-hidden="true" /></span>
      </div>
    </Link>
  </article>
);

const SmallCard = ({ blog }) => (
  <article className="lp-card lp-card--lift lp-blog-small">
    <Link to={blogHref(blog)} state={{ id: blog.id }} className="lp-blog-small__link" aria-label={`Read ${blog.title}`}>
      <div className="lp-blog-small__thumb">
        <BlogImage blog={blog} />
      </div>
      <div className="lp-blog-small__body">
        <p className="lp-blog__meta">
          {blog.date && <span>{blog.date}</span>}
          <span>{blog.readTime}</span>
        </p>
        <h3 className="lp-blog__title lp-clamp-2">{blog.title}</h3>
      </div>
    </Link>
  </article>
);

const Skeleton = () => (
  <div className="row g-4" role="status" aria-label="Loading articles">
    <div className="col-12 col-lg-7">
      <div className="lp-card" aria-hidden="true">
        <div className="lp-skeleton lp-blog__media" />
        <div className="p-4">
          <div className="lp-skeleton lp-skeleton--line w-50" />
          <div className="lp-skeleton lp-skeleton--line lp-skeleton--lg w-100" />
          <div className="lp-skeleton lp-skeleton--line w-75" />
        </div>
      </div>
    </div>
    <div className="col-12 col-lg-5 d-grid gap-3 align-content-start" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="lp-card p-3"><div className="lp-skeleton lp-skeleton--line lp-skeleton--lg w-100" /><div className="lp-skeleton lp-skeleton--line w-50 mb-0" /></div>
      ))}
    </div>
  </div>
);

/** Hidden completely when there are no published articles. */
const BlogSection = () => {
  const { status, blogs, retry } = useLatestBlogs();
  if (status === "empty") return null;

  const [featured, ...rest] = blogs;

  return (
    <section className="lp-section" aria-labelledby="lp-blog-title">
      <div className="container">
        <div className="lp-heading-row">
          <SectionHeading
            id="lp-blog-title"
            eyebrow="Articles & insights"
            title="Latest From Our Knowledge Hub"
            description="Guides and market insights for buyers, sellers and renters."
            align="start"
          />
          <Link to="/blogs" className="lp-btn lp-btn--outline-dark flex-shrink-0">
            Explore All Articles
          </Link>
        </div>

        {status === "loading" && <Skeleton />}

        {status === "error" && (
          <div className="lp-empty" role="alert">
            <ExclamationTriangle aria-hidden="true" />
            <p>We couldn&apos;t load the latest articles right now.</p>
            <button type="button" className="lp-btn lp-btn--outline-dark" onClick={retry}>Try again</button>
          </div>
        )}

        {status === "success" && (
          <div className="row g-4">
            <div className={rest.length ? "col-12 col-lg-7" : "col-12"}>
              <FeaturedCard blog={featured} />
            </div>
            {rest.length > 0 && (
              <div className="col-12 col-lg-5 d-grid gap-3 align-content-start">
                {rest.map((blog) => <SmallCard key={blog.key} blog={blog} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
export { BlogImage };
